import React, { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';
import { PersonalInformation } from './forms/PersonalInformation';
import {
    formatProperNames,
    getAppData,
    callDataExposureApi,
    getAcquisitionFlow,
    AcquisitionFlow,
    clearSessionAndRedirectToDataEntryPage,
    checkIfInValidAcquisitionFlow,
    getProgramData,
    getMarketingAttributesForAcquisitions,
    submitConsent,
    getIpAddress,
    getBriteVerifyPhoneValidationResult,
} from '../../utils/acquisition-utils';
import SessionStorage from '../../utils/SessionStorage';
import { AcquisitionsLoader } from '../AcquisitionsLoader/AcquisitionsLoader';
import { acquisitionApiRequest } from '../../utils/acquisition-api/AcquisitionApi';
import { usePushdownContext } from '../../providers/Pushdown/PushdownProvider';
import { API_MAPPING } from '../../utils/api-utils/api-url-map';
import { flowDataModule } from '../../utils/flowDataModule';
import axios from 'axios';

import './ApplicationVerification.css';
import { cleanNumericInput } from '../../utils/income-utils';
import { TValidateSsnResponse } from '../../types/responses/ValidateSsnResponse';
import { TValidateSsnRequest } from '../../types/requests/ValidateSsnRequest';
import { blackboxUtils } from '../../utils/blackbox-utils';
import IncludeXFForm from './forms/IncludeXFForm';
import { callAcquisitionBff } from 'utils/acquisition-api/AcquisitionBff';
import { SubmitApplicationRequest } from 'types/requests/submit-application';
import { wrapTrademarkSuperscript } from '../../utils/cob-utils';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';
import { useFeatureFlags } from '../../providers/FeatureFlagProvider/FeatureFlagProvider';
import { Button } from '../../importCL';
import { useZoomChecker } from '../../hooks/useZoomChecker';

type TFormSectionPropType = { key: string, value: string };

interface IFormSection {
    sectionTitle?: string;
    sectionType?: string;
    props?: TFormSectionPropType[];
    experienceFragment?: any;
}

export interface IApplicationVerificationProps extends MappedComponentProperties {
    hiddenOnApplication?: boolean;
    headline?: string;
    generalErrorCode?: string;
    serverErrorCode?: string;
    invalidZipErrorCode?: string;
    ssnMismatchErrorCode?: string;
    dataEntryPath?: string;
    fullAppPath?: string;
    counterOfferRedirect?: string;
    limitedEsignRedirect?: string;
    riskBasedEsignRedirect?: string;
    approvedRedirect?: string;
    pendingRedirect?: string;
    levelThreeRedirect?: string;
    customerDocVerifyRedirect?: string;
    otpRedirect?: string;
    mobileWalletRedirect?: string;
    authorizedUserErrorCode?: string;
    sections?: IFormSection[];
    approvedPendingFundingRedirect?: string;
}

export interface IApplicationVerificationElement {
    inEditMode: boolean;
}

export const APP_VERIFICATION_EVENT = 'appVerificationEvent';

export const ApplicationVerificationEditConfig = {
    emptyLabel: 'Application Verification',
    isEmpty: (props: PropsWithChildren<IApplicationVerificationProps>) => {
        return !props || !props.headline || !props.sections?.length;
    },
};

const submitApiNames = {
    preQual: 'pq-full-submit',
    skipPQ: 'skip-pq-full-submit',
    [AcquisitionFlow.SkipPQCounter]: 'skip-pq-full-submit',
    preApproved: 'pa-full-submit',
    pqOne: 'skip-pq-full-submit',
    // This is in the new mapping file api-url-map.ts
    partnerHosted: 'submit-application',
};

const approvedApiNames = {
    preQual: 'pq-app-approved',
    skipPQ: 'skip-pq-app-approved',
    [AcquisitionFlow.SkipPQCounter]: 'skip-pq-app-approved',
    preApproved: 'pa-app-approved',
    pqOne: 'skip-pq-app-approved',
    // new API's for pha don't need this
};

const pendingApiNames = {
    preQual: 'pq-app-pending',
    skipPQ: 'skip-pq-app-pending',
    [AcquisitionFlow.SkipPQCounter]: 'skip-pq-app-pending',
    preApproved: 'pa-app-pending',
    pqOne: 'skip-pq-app-pending',
    // new API's for pha don't need this
};

const ApplicationVerification = (props: PropsWithChildren<IApplicationVerificationProps>): JSX.Element => {
    const sectionsRef = useRef<Array<IApplicationVerificationElement | null>>([]);
    const [isSectionInEditMode, setIsSectionInEditMode] = useState<boolean>(false);
    const [isSpinning, setIsSpinning] = useState<boolean>(false);
    const [processingSteps, setProcessingSteps] = useState<string[]>([]);
    const isSignedEsign = SessionStorage.verifyNotEmpty('isEsign') && SessionStorage.getObject('isEsign');
    const isSignedLimitedEsign = SessionStorage.verifyNotEmpty('visitedEsign') && SessionStorage.getObject('visitedEsign');
    const { getFeatureFlags } = useFeatureFlags();
    const isZoomedIn = useZoomChecker();

    const declineRedirect = (flow: AcquisitionFlow) => {
        switch (flow) {
            case AcquisitionFlow.PQ:
                return '/content/cob-corp-acquisition/us/en/pre-qualification/origination-result/application-declined';
            case AcquisitionFlow.SkipPQ:
            case AcquisitionFlow.SkipPQCounter:
                return '/content/cob-corp-acquisition/us/en/full-application/application-result/application-declined';
            case AcquisitionFlow.PA:
                return '/content/cob-corp-acquisition/us/en/pre-approved/fulfillment-result/application-denied';
            case AcquisitionFlow.PqOfOne:
                return '/content/cob-corp-acquisition/us/en/pq-one/origination-result/application-declined';
            case AcquisitionFlow.PHA:
                return '/content/cob-corp-acquisition/us/en/pha-secured/origination-result/application-declined';
        }
    };

    const { showPushdown, closePushdown } = usePushdownContext();

    const handleAppData = (appData: Record<string, any>): { formattedData: Record<string, any>, processingList: Array<string> } => {
        const processingList = ['Validating personal information', 'Assigning your card design'];
        const acquisitionFlow = getAcquisitionFlow();
        const deviceInfo = blackboxUtils.getBlackbox();
        if (
            checkIfInValidAcquisitionFlow([
                AcquisitionFlow.PQ,
                AcquisitionFlow.SkipPQ,
                AcquisitionFlow.SkipPQCounter,
                AcquisitionFlow.PqOfOne,
            ], acquisitionFlow)
        ) {
            const isMailingHomeAddressSame = typeof appData.PersonalInformation.IsMailingHomeAddressSame === 'boolean' ? appData.PersonalInformation.IsMailingHomeAddressSame : true;
            let formattedData = {
                ...appData,
                AuthorizedUser: {
                    ...appData.AuthorizedUser,
                    AuthorizedUserDOBDay: appData.AuthorizedUser.AuthorizedUserDOBDay ? parseInt(appData.AuthorizedUser.AuthorizedUserDOBDay) : null,
                    AuthorizedUserDOBMonth: appData.AuthorizedUser.AuthorizedUserDOBMonth ? parseInt(appData.AuthorizedUser.AuthorizedUserDOBMonth) : null,
                    AuthorizedUserDOBYear: appData.AuthorizedUser.AuthorizedUserDOBYear ? parseInt(appData.AuthorizedUser.AuthorizedUserDOBYear) : null,
                    AuthorizedUserFirstName: formatProperNames(appData.AuthorizedUser.AuthorizedUserFirstName),
                    AuthorizedUserMiddleInitial: formatProperNames(appData.AuthorizedUser.AuthorizedUserMiddleInitial),
                    AuthorizedUserLastName: formatProperNames(appData.AuthorizedUser.AuthorizedUserLastName),
                },
                PartnerOfferId: getAcquisitionFlow() === AcquisitionFlow.PqOfOne ? SessionStorage.get('offerId') : undefined,
                PersonalInformation: {
                    ...appData.PersonalInformation,
                    Ssn: getAcquisitionFlow() === AcquisitionFlow.PqOfOne ? '' : appData.PersonalInformation.Ssn,
                    MonthlyIncome: [AcquisitionFlow.SkipPQ, AcquisitionFlow.SkipPQCounter, AcquisitionFlow.PqOfOne].includes(getAcquisitionFlow()) ? parseInt(appData.PersonalInformation.MonthlyIncome.replace(/[$,]/g, '')) : appData.PersonalInformation.MonthlyIncome,
                    DOBDay: parseInt(appData.PersonalInformation.DOBDay),
                    DOBMonth: parseInt(appData.PersonalInformation.DOBMonth),
                    DOBYear: parseInt(appData.PersonalInformation.DOBYear),
                    FirstName: formatProperNames(appData.PersonalInformation.FirstName),
                    MiddleInitial: formatProperNames(appData.PersonalInformation.MiddleInitial),
                    LastName: formatProperNames(appData.PersonalInformation.LastName),
                    IsMailingHomeAddressSame: isMailingHomeAddressSame,
                    HomeAddressLine1: isMailingHomeAddressSame ? appData.PersonalInformation.MailingAddressLine1 : appData.PersonalInformation.HomeAddressLine1,
                    HomeAddressLine2: isMailingHomeAddressSame ? appData.PersonalInformation.MailingAddressLine2 : appData.PersonalInformation.HomeAddressLine2,
                    HomeCity: isMailingHomeAddressSame ? appData.PersonalInformation.MailingCity : appData.PersonalInformation.HomeCity,
                    HomeState: isMailingHomeAddressSame ? appData.PersonalInformation.MailingState : appData.PersonalInformation.HomeState,
                    HomeZipCode: isMailingHomeAddressSame ? appData.PersonalInformation.MailingZipCode : appData.PersonalInformation.HomeZipCode,
                    ioBlackBox: appData.PersonalInformation.ioBlackBox ? appData.PersonalInformation.ioBlackBox : deviceInfo?.Blackbox?.ThirdParty,
                    fpBB: appData.PersonalInformation.fpBB ? appData.PersonalInformation.fpBB : deviceInfo?.Blackbox?.FirstParty,
                },
                MobilePhoneNumber: {
                    ...appData.MobilePhoneNumber,
                    Agree: appData.MobilePhoneNumber.Agree === 'true',
                },
                IsCounterOffer: SessionStorage.tryGetObject('isCounterOffer', false),
                MarketingAttributes: getMarketingAttributesForAcquisitions(),
            };

            //Add funding information for request if we are in funding flow
            if (appData.IsSecuredCard) {
                const fundingInformation = {
                    'FundingPaymentSource': 0,
                    'FundingInformation': {
                        'AccountNumber': '',
                        'AccountType': '',
                        'AmountToFund': 0.0,
                        'RoutingNumber': '',
                        'C1BAuthToFund': false,
                        'SecuredMinimumCreditLine': 0,
                        'SecuredMaximumCreditLine': 0,
                        'SecuredCreditLineIncrement': 0,
                        'Errors': [],
                    },
                    'DebitFundingInformation': null,
                };
                formattedData = {
                    ...formattedData,
                    ...fundingInformation,
                };
            }

            if (appData.CreditProtectionSelected) {
                processingList.push('Enrolling you in Credit Protection');
            }
            if (appData.AuthorizedUser.IsAuthorizedUserSelected) {
                processingList.push('Adding your Authorized User');
            }

            return { formattedData, processingList };
        }
        const applicantDob = appData.Applicant.DateOfBirth;
        // PA flow requires monthlyIncome to be a number. All other flows should be a string.
        const formattedIncome = acquisitionFlow === AcquisitionFlow.PA ?
            parseInt(cleanNumericInput(appData.Applicant.MonthlyIncome)) :
            appData.Applicant.MonthlyIncome;
        const formattedData = {
            ...appData,
            Applicant: {
                ...appData.Applicant,
                DateOfBirth: applicantDob.slice(6, 10) + '-' + applicantDob.slice(0, 2) + '-' + applicantDob.slice(3, 5) + 'T00:00:00',
                MonthlyIncome: formattedIncome,
            },
        };

        if (appData.CreditProtection.CreditProtectionSelected) {
            processingList.push('Enrolling you in Credit Protection');
        }
        if (appData.RushProcess) {
            processingList.push('Upgrading you to Rush Processing');
        }
        if (appData.AuthorizedUser.IsAuthorizedUserSelected) {
            processingList.push('Adding your Authorized User');
        }
        return { formattedData, processingList };
    };

    /** building submit request body for new API's */
    const handleFlowApplicationData = async (): Promise<{ formattedData: Record<string, any>, processingList: Array<string> }> => {
        const processingList = ['Validating personal information', 'Assigning your card design'];
        const applicationOfferResponse = SessionStorage.tryGetObject('applicationOfferResponse', {});
        const flowApplicationData = SessionStorage.tryGetObject('flowApplicationData', {});
        const applicationRequestBody: Partial<SubmitApplicationRequest> = {};
        const deviceInfo = blackboxUtils.getBlackbox();

        if (getAcquisitionFlow() !== AcquisitionFlow.PHA) {
            return { formattedData: applicationRequestBody, processingList };
        }

        if (flowApplicationData.creditProtectionSelected) {
            processingList.push('Enrolling you in Credit Protection');
        }
        if (flowApplicationData.AuthorizedUser.IsAuthorizedUserSelected) {
            processingList.push('Adding your Authorized User');
        }

        applicationRequestBody.application = { applicationType: flowApplicationData?.applicationType};        
        // Do not add reservation number so that the new reservation number is generated and returned as per IT.
        // applicationRequestBody.application.reservationNumber = flowApplicationData?.reservationNumber;

        applicationRequestBody.offer = {};
        applicationRequestBody.offer.offerId = flowApplicationData.offers[0].offerId;
        // Do not add reservation number so that the new reservation number is generated and returned as per IT.
        // applicationRequestBody.offer.reservationNumber = flowApplicationData.reservationNumber;
        applicationRequestBody.parties = [{ ...flowApplicationData.parties[0] }];

        applicationRequestBody.card = {};
        applicationRequestBody.card.enrollments = {
            creditProtectionEnrolled: flowApplicationData.creditProtectionSelected ?? false,
            authorizedUserEnrolled: flowApplicationData.AuthorizedUser.IsAuthorizedUserSelected ?? false,
            disclosure: {
                disclosureVersion: flowApplicationData.ESignDisclosureVersion,
                timestamp: Date.now(),
                disclosureConsentAgreed: flowApplicationData.AgreedToFullESign ?? false,
            },
        };

        if (flowApplicationData?.AuthorizedUser?.IsAuthorizedUserSelected) {
            applicationRequestBody.card.enrollments.authorizedUsers = [{
                firstName: flowApplicationData.AuthorizedUser.AuthorizedUserFirstName,
                lastName: flowApplicationData.AuthorizedUser.AuthorizedUserLastName,
                middleInitial: flowApplicationData.AuthorizedUser.AuthorizedUserMiddleInitial,
                dateOfBirth: `${flowApplicationData.AuthorizedUser.AuthorizedUserDOBYear}-${flowApplicationData.AuthorizedUser.AuthorizedUserDOBMonth}-${flowApplicationData.AuthorizedUser.AuthorizedUserDOBDay}`,
                spouse: flowApplicationData.AuthorizedUser.IsAuthorizedUserSpouse,
                prefix: '',
                suffix: '',
            }];
        }

        if (flowApplicationData.creditProtectionSelected) {
            applicationRequestBody.card.enrollments.creditProtectionInitials = flowApplicationData.creditProtectionInitials ?? '';
        }

        applicationRequestBody.partnerData = { ...applicationOfferResponse.partnerData };
        applicationRequestBody.requestMetadata = {
            requestDateTime: Math.floor(new Date().getTime() / 1000),
            partyMetadata: {
                ipAddress: await getIpAddress(),
                blackBox: [deviceInfo.Blackbox.FirstParty, deviceInfo.Blackbox.ThirdParty].join(';'),
            },
        };
        applicationRequestBody.trafficAttribution = [...applicationOfferResponse.trafficAttribution];        
        const selectedCardIndex = flowApplicationData.SelectedProgramPlasticCode - 1;
        const cardData = applicationOfferResponse?.card?.keyCardFeatures?.cardVariations[selectedCardIndex];
        applicationRequestBody.card.cardVariation = cardData;

        return { formattedData: applicationRequestBody, processingList };
    };

    const getDynamicCounterOfferData = async (programCode: string) => {
        const { data } = await axios.get(`/services/appoffermodule?programCode=${programCode}`);
        SessionStorage.setObject('offerContent', data);
        return 'done';
    };

    const dataExposureWrapper = (flow: AcquisitionFlow, redirect?: string) => {
        if (flow === AcquisitionFlow.PHA) {
            return; // new api's for PHA don't require this call
        }

        callDataExposureApi(flow).then((result) => {
            if (!result) {
                throw result;
            }
            if (redirect) {
                window.location.assign(redirect + window.location.search);
            }
        }).catch(() => {
            clearSessionAndRedirectToDataEntryPage('lost', props.dataEntryPath, 'Data exposure API failed');
        });
    };

    const showPushdownOrProcessingError = (pushdownCode: string | undefined) => {
        if (!pushdownCode) {
            console.error('Pushdown code not defined');
            showPushdown('processingError');
            return;
        }

        showPushdown(pushdownCode);
    };

    const handleFormSubmit = async () => {
        closePushdown();
        const flow = getAcquisitionFlow();
        const submitApiName = submitApiNames[flow];
        const appData = await getAppData();
        const { formattedData, processingList } = flow === AcquisitionFlow.PHA ? await handleFlowApplicationData() : handleAppData(appData);
        setProcessingSteps(processingList);
        setIsSpinning(true);

        if (getFeatureFlags('DPM-10937-SubmitPhoneValidationResults') && SessionStorage.verifyNotEmpty('bvrslt')) {
            const result = getBriteVerifyPhoneValidationResult();
            const phoneNumber = formattedData?.PersonalInformation?.PrimaryPhoneNumber || formattedData?.Applicant?.PrimaryPhoneNumber || SessionStorage.get('phoneNumber');
            if (result && result !== 'skipped' && phoneNumber) {
                const phones = [
                    {
                        value: phoneNumber?.Number || phoneNumber,
                        primary: true,
                        verified: false,
                        usageType: 'UNKNOWN',
                        serviceType: result?.phone?.service_type?.toUpperCase() || 'UNKNOWN',
                        location: result?.phone?.phone_location?.toUpperCase() || 'UNKNOWN',
                    },
                ];
                if (formattedData?.Applicant) {
                    formattedData.Applicant = {
                        ...formattedData.Applicant,
                        phones,
                    };
                }
                if (formattedData?.PersonalInformation) {
                    formattedData.PersonalInformation = {
                        ...formattedData.PersonalInformation,
                        phones,
                    };
                }
            }
        }

        if (flow === AcquisitionFlow.PHA) {
            callAcquisitionBff('submit-application', {
                body: formattedData,
            }).then(async (response: any) => {
                if (!response) {
                    throw { response };
                }

                const responseData = response.data;
                let flowApplicationData = SessionStorage.tryGetObject('flowApplicationData', {});

                flowApplicationData = {
                    ...flowApplicationData,
                    ...responseData,
                };

                SessionStorage.setObject('flowApplicationData', flowApplicationData);
                // TODO: we need feedback from DURGA on what she wants/needs for this so we can translate from the new api correctly
                await flowDataModule.updateInSession({
                    ...responseData.application,
                    ...responseData.applicationStatus,
                    ...responseData.parties,
                    ...responseData.card,
                    ...(appData?.IsSecuredCard && {
                        AgreedToFullESign: appData.AgreedToFullESign,
                        AgreedToPrivacyPolicy: appData.AgreedToPrivacyPolicy,
                        ApplicantESignInterest: appData.ApplicantESignInterest,
                    }),
                });

                if (!flowApplicationData.applicationStatus) {
                    showPushdownOrProcessingError(props.generalErrorCode!);
                    setIsSpinning(false);
                    return;
                }

                switch (flowApplicationData.applicationStatus.applicationStatus) {
                    case 'Approved': {
                        const consent = await submitConsent(flowApplicationData);
                        consent ? window.location.assign(props.approvedRedirect + window.location.search) : clearSessionAndRedirectToDataEntryPage(props.generalErrorCode!);
                        break;
                    }

                    case 'Declined':
                    case 'SoftDecline':
                    case 'HardDecline': {
                        SessionStorage.remove('isCounterOffer');
                        const consent = await submitConsent(flowApplicationData);
                        consent ? window.location.assign(declineRedirect(flow) + window.location.search) : clearSessionAndRedirectToDataEntryPage(props.generalErrorCode!);
                        break;
                    }

                    case 'Pending':
                    case 'Queued': {
                        const consent = await submitConsent(flowApplicationData);
                        consent ? window.location.assign(props.pendingRedirect + window.location.search) : clearSessionAndRedirectToDataEntryPage(props.generalErrorCode!);
                        break;
                    }

                    case 'StepUp': {
                        const stepUpRedirects = {
                            'KIQ': props.levelThreeRedirect,
                            'OTP': props.otpRedirect,
                            'MITEK': props.customerDocVerifyRedirect,
                        };

                        const redirectUrl = stepUpRedirects[flowApplicationData.stepUpType];
                        if (redirectUrl) {
                            window.location.assign(redirectUrl + window.location.search);
                        } else {
                            return clearSessionAndRedirectToDataEntryPage('appInProgress');
                        }
                        break;
                    }

                    default: {
                        showPushdownOrProcessingError(props.generalErrorCode!);
                        setIsSpinning(false);
                    }
                }
            }).catch((err) => {
                const status = err?.response?.status;
                const flowApplicationData = SessionStorage.tryGetObject('flowApplicationData', {});

                if (flowApplicationData.expirationDateTime < Math.floor(Date.now() / 1000)) {
                    clearSessionAndRedirectToDataEntryPage('pqOfOne');
                    return;
                }

                if (status === 401 || status === 408) {
                    clearSessionAndRedirectToDataEntryPage('lost', props.dataEntryPath, `Status code ${status}`);
                    return;
                }

                if (/SSN/.test(err?.message)) {
                    showPushdownOrProcessingError(props.ssnMismatchErrorCode);
                    return;
                }

                clearSessionAndRedirectToDataEntryPage('processingError', props.dataEntryPath);
                return;
            });

            return;
        }

        if (flow === AcquisitionFlow.PA) {
            if (!SessionStorage.verifyNotEmpty('preApprovalFullAppData')) {
                showPushdownOrProcessingError(props.generalErrorCode);
                setIsSpinning(false);
                return;
            }

            try {
                const response = await acquisitionApiRequest<TValidateSsnResponse>('pa-validate-ssn', {
                    method: 'POST',
                    body: {
                        ApprovalCode: appData.ReservationNumber,
                        FullSsn: appData.Applicant.SocialSecurityNumber.FullSocialSecurityNumber,
                    } as TValidateSsnRequest,
                });
                if (response.status !== 200 || response.data.Result !== 3) {
                    throw { response };
                }
            } catch (error) {
                const response = (error as any)?.response ? (error as any).response : error;
                if (response.data) {
                    switch (response.data.Result) {
                        case 0: // UnableToFindMatch
                            showPushdownOrProcessingError(props.ssnMismatchErrorCode);
                            break;
                        case 4: // InvalidCodeOrSsn
                            SessionStorage.setObject('ssnVerifyError', response.data);
                            window.location.assign(`${props.fullAppPath + (window.location.search ? window.location.search + '&' : '?') + 'verification=on'}}`);
                            break;
                        case 5: // ExceededAttemptsInSession
                        case 10: // MaxAttemptsExceeded
                            clearSessionAndRedirectToDataEntryPage('lost', props.dataEntryPath, 'Max attempts exceeded');
                            break;
                        default: // Unexpected Response
                            showPushdownOrProcessingError(props.serverErrorCode!);
                            break;
                    }
                } else {
                    showPushdownOrProcessingError(props.generalErrorCode!);
                }
                setIsSpinning(false);
                return;
            }
        }

        if (flow === AcquisitionFlow.SkipPQ && !Object.keys(getProgramData()).length && !SessionStorage.verifyNotEmpty('cardInfo')) {
            clearSessionAndRedirectToDataEntryPage('processingError', props.dataEntryPath);
            return;
        }

        acquisitionApiRequest<any>(submitApiName as keyof typeof API_MAPPING, {
            method: 'POST',
            body: formattedData,
        }).then(async (response) => {
            if (response.status !== 200) {
                throw {
                    response: response,
                };
            }

            let responseJson: any;
            try {
                responseJson = JSON.parse(JSON.stringify(response.data));
            } catch {
                showPushdown(props.generalErrorCode as string);
                setIsSpinning(false);
            }

            if (!responseJson) {
                return;
            }

            await SessionStorage.setCipherWithCurrentScope('fullAppResponse', responseJson);
            await flowDataModule.updateInSession(responseJson.AdditionalInformation);
            if (formattedData.IsCounterOffer) {
                SessionStorage.set('afterCounterOffer', 'true');
            }
            if ([0, 11].includes(responseJson.SubmitApplicationEnum)) { // Error
                throw {
                    response: response,
                };
            } else if ([1, 5].includes(responseJson.SubmitApplicationEnum)) { //DeclinedWithESignCredit, DeclinedWithESignMLA
                if (
                    checkIfInValidAcquisitionFlow([
                        AcquisitionFlow.SkipPQ,
                        AcquisitionFlow.PqOfOne,
                    ], flow)
                    && responseJson.ApplicationInformation
                    && responseJson.ApplicationInformation.IsCounterOffer
                ) {
                    const tempAppInfo = [];
                    tempAppInfo.push(responseJson.ApplicationInformation);
                    const counterOfferData = {
                        ...responseJson,
                        ApplicationInformation: tempAppInfo,
                    };
                    await SessionStorage.setCipherWithCurrentScope('prequalResponse', counterOfferData);
                    SessionStorage.setObject('cardInfo', responseJson.ApplicationInformation.CardDesign.Cards);
                    SessionStorage.set('selectedProgramCode', responseJson.ApplicationInformation.ProgramRates.ProgramCode);
                    SessionStorage.set('isCounterOffer', 'true');
                    const { PersonalInformation } = await getAppData();
                    await SessionStorage.setCipherWithCurrentScope('personalInfo', PersonalInformation);
                    SessionStorage.remove('prequalFullAppData');
                    await getDynamicCounterOfferData(responseJson.ApplicationInformation.ProgramRates.ProgramCode);
                    if (isSignedEsign || isSignedLimitedEsign) {
                        dataExposureWrapper(flow, '/content/cob-corp-acquisition/us/en/full-application/application/counter-application');
                    } else {
                        dataExposureWrapper(flow, props.counterOfferRedirect);
                    }
                } else {
                    SessionStorage.remove('isCounterOffer');
                    const redirect = isSignedEsign || isSignedLimitedEsign ? declineRedirect(flow) : props.limitedEsignRedirect;
                    dataExposureWrapper(flow, redirect);

                }
            } else if ([2, 3, 4].includes(responseJson.SubmitApplicationEnum)) { //DeclinedWithESignCreditNoScore, DeclinedWithESignNonCredit, DeclinedWithESignNCDLetter
                SessionStorage.remove('isCounterOffer');
                const redirect = isSignedEsign || isSignedLimitedEsign ? declineRedirect(flow) : props.limitedEsignRedirect;
                dataExposureWrapper(flow, redirect);
            } else if (responseJson.SubmitApplicationEnum === 7) { // Approved
                const approvedApiName = approvedApiNames[flow];
                acquisitionApiRequest<any>(approvedApiName as keyof typeof API_MAPPING, {
                    method: 'GET',
                }).then(async (response) => {
                    if (response.status !== 200) {
                        throw response;
                    }
                    const approvedData = response.data;

                    const instantIssuanceOffer = approvedData.InstantIssuanceOffer;
                    await SessionStorage.setCipher('app-approved-data', approvedData);
                    let flowDataObj;
                    if (appData?.IsSecuredCard) {
                        flowDataObj = {
                            ...approvedData.AdditionalInformation,
                            AgreedToFullESign: appData.AgreedToFullESign,
                            AgreedToPrivacyPolicy: appData.AgreedToPrivacyPolicy,
                            ApplicantESignInterest: appData.ApplicantESignInterest,
                        };
                    } else {
                        flowDataObj = {
                            ...approvedData.AdditionalInformation,
                        };
                    }
                    await flowDataModule.updateInSession(flowDataObj);
                    if (appData.IsSecuredCard) {
                        dataExposureWrapper(flow, props.approvedPendingFundingRedirect);
                    } else {
                        switch (instantIssuanceOffer) {
                            case 0: // No Instant Issuance Offer
                                if (
                                    responseJson.IsRiskBasedPricingNoticeRequired &&
                                    !appData.IsSecuredCard &&
                                    !appData.IsCounterOffer
                                ) {
                                    //redirect to risk based final page
                                    const redirect = isSignedEsign || isSignedLimitedEsign ? props.approvedRedirect : props.riskBasedEsignRedirect;
                                    dataExposureWrapper(flow, redirect);
                                } else {
                                    dataExposureWrapper(flow, props.approvedRedirect);
                                }
                                break;
                            case 1: // Printed Card Pickup
                                clearSessionAndRedirectToDataEntryPage('lost', props.dataEntryPath, 'Printed card pickup');
                                break;
                            case 2: { // Mobile Wallet
                                SessionStorage.set('rbPricingRequired', responseJson.IsRiskBasedPricingNoticeRequired ? 'true' : 'false');
                                const redirect = responseJson.IsRiskBasedPricingNoticeRequired && !isSignedEsign && !isSignedLimitedEsign ? props.riskBasedEsignRedirect : props.mobileWalletRedirect;
                                dataExposureWrapper(flow, redirect);
                                break;
                            }
                            default:
                                SessionStorage.remove('app-approved-data');
                                clearSessionAndRedirectToDataEntryPage('lost', props.dataEntryPath, 'Default instant issuance offer error');
                                break;
                        }
                    }
                }).catch(() => {
                    showPushdown('processingError');
                });
            } else if (responseJson.SubmitApplicationEnum === 8) { // Pending
                const pendingApiName = pendingApiNames[flow];
                acquisitionApiRequest<any>(pendingApiName as keyof typeof API_MAPPING, {
                    method: 'GET',
                }).then(async (response) => {
                    if (response.status !== 200) {
                        throw response;
                    }
                    const pendingData = response.data;
                    await SessionStorage.setCipher('app-pending-data', pendingData);
                    await flowDataModule.updateInSession(pendingData.AdditionalInformation);
                    dataExposureWrapper(flow, props.pendingRedirect);
                }).catch(() => {
                    showPushdown('processingError');
                });
            } else if (responseJson.SubmitApplicationEnum === 9) { // Authorize
                acquisitionApiRequest<any>('application-authorized', {
                    method: 'GET',
                }).then(async (response) => {
                    if (response.status !== 200) {
                        throw response;
                    }
                    const authorizeData = response.data;
                    await SessionStorage.setCipher('appAuthorizedResponse', authorizeData);
                    const nextStepEnum = authorizeData.NextAction;
                    let redirect;
                    if (nextStepEnum === 1) {
                        redirect = props.levelThreeRedirect;
                    } else if (nextStepEnum === 2) {
                        redirect = props.otpRedirect;
                    } else if ([3, 4].includes(nextStepEnum)) {
                        redirect = props.customerDocVerifyRedirect;
                    } else if (nextStepEnum === 0) {
                        return clearSessionAndRedirectToDataEntryPage('appInProgress');
                    }

                    if (redirect) {
                        dataExposureWrapper(flow, redirect);
                    } else {
                        SessionStorage.remove('appAuthorizedResponse');
                        clearSessionAndRedirectToDataEntryPage('lost', undefined, 'Missing redirect');
                    }
                }).catch(() => {
                    showPushdown('processingError');
                });
            } else { // Unhandled Enum
                throw response;
            }
        }).catch(error => {
            const err = error.response;
            if (!err || !err.status || err.status === 500) {
                clearSessionAndRedirectToDataEntryPage('processingError', props.dataEntryPath);
            } else if ([401, 408].includes(err.status)) {
                clearSessionAndRedirectToDataEntryPage('lost', props.dataEntryPath, `Status code ${err.status}`);
            } else {
                let responseJson;
                try {
                    responseJson = JSON.parse(JSON.stringify(err.data));
                } catch {
                    showPushdown(props.generalErrorCode as string);
                    setIsSpinning(false);
                }

                if (!responseJson) {
                    return;
                }

                let errors = [];
                const overrideError = { genericError: false, processingError: false, samePrimaryAndAuthUser: false };
                if (responseJson.ValidationError && responseJson.ValidationError.length) {
                    SessionStorage.setObject('valRes', responseJson.ValidationError);
                    errors = responseJson.ValidationError;
                } else if (responseJson.Errors && responseJson.Errors.length) {
                    errors = responseJson.Errors;
                }

                const pushdownMessages = [];
                const samePrimaryAndAuthUserRegex = /primary card member cannot be added as the authorized user/i;
                for (const error of errors) {
                    if (
                        error.ErrorMessage === 'Cannot process FullAppSubmit in ApplicationService' ||
                        error.ErrorMessage === 'Cannot process full application submit'
                    ) {
                        overrideError.genericError = true;
                    } else if (error === 'processingError') {
                        overrideError.processingError = true;
                    } else if (samePrimaryAndAuthUserRegex.test(error.ErrorMessage.toString())) {
                        overrideError.samePrimaryAndAuthUser = true;
                    } else {
                        pushdownMessages.push(error.ErrorMessage);
                    }
                }

                if (overrideError.genericError) {
                    showPushdown(props.generalErrorCode as string);
                } else if (overrideError.processingError) {
                    showPushdown('processingError');
                } else if (overrideError.samePrimaryAndAuthUser) {
                    showPushdown(props.authorizedUserErrorCode as string);
                } else {
                    showPushdown({
                        color: 'error',
                        header: 'Problem Processing Your Request.',
                        description: 'The following fields and/or sections require your attention:',
                        message: pushdownMessages,
                    });
                }
                callDataExposureApi(flow).then((result: boolean) => {
                    if (!result) {
                        throw result;
                    }
                    setIsSpinning(false);
                }).catch(() => {
                    clearSessionAndRedirectToDataEntryPage('lost', props.dataEntryPath, 'Data exposure API failed');
                });
            }
        });
    };

    useEffect(() => {
        if (props.sections) {
            sectionsRef.current = sectionsRef.current.slice(0, props.sections.length);
        }
        window.addEventListener(APP_VERIFICATION_EVENT, () => {
            let inEditMode = false;
            sectionsRef.current.forEach(el => {
                if (el && el.inEditMode) {
                    inEditMode = true;
                }
            });
            setIsSectionInEditMode(inEditMode);
        });
    }, [props.sections]);

    return (
        <>
            {isSpinning ? (
                <AcquisitionsLoader processingSteps={processingSteps} isZoomedIn={isZoomedIn} />
            ) : (
                <div id='application-verification' className={`ApplicationVerification ApplicationVerification__formWrapper ${getAcquisitionFlow() === AcquisitionFlow.PQ ? 'ApplicationVerification__formWrapper--preQual' : ''}`}>
                    <div className='ApplicationVerification__formArea'>
                        <h2 className='ApplicationVerification__headline'>{transformHTMLParserLinks(wrapTrademarkSuperscript(props.headline))}</h2>
                        {props.sections?.map((section: IFormSection, index: number) => {
                            const componentProps: Record<string, string> = {};
                            section.props?.forEach((prop: { key: string, value: string }) => {
                                componentProps[prop.key] = prop.value;
                            });
                            if (section.sectionType === 'personalInformation') {
                                return (
                                    <PersonalInformation key={index} {...componentProps} sectionTitle={section.sectionTitle} ref={el => sectionsRef.current[index] = el} isZoomedIn={isZoomedIn} />
                                );
                            } else if (props.hiddenOnApplication && !props.isInEditor &&
                                (section.sectionType === 'cardDesign' || section.sectionType === 'creditProtectionProgram')) {
                                return (<></>);
                            }
                            return (
                                <IncludeXFForm
                                    {...componentProps}
                                    key={index}
                                    ref={(el: IApplicationVerificationElement | null) => sectionsRef.current[index] = el}
                                    sectionType={section.sectionType}
                                    sectionTitle={section.sectionTitle}
                                    fragmentPath={props.cqPath + '/experienceFragment'}
                                    cqItems={section.experienceFragment ? section.experienceFragment[':items'] : undefined}
                                    cqItemsOrder={section.experienceFragment ? section.experienceFragment[':itemsOrder'] : undefined}
                                    isZoomedIn={isZoomedIn}
                                />
                            );
                        })}
                        <section className='ApplicationVerification__formSection ApplicationVerification__formSection--submitContainer'>
                            <div className='ApplicationVerification__submitContainer'>
                                {isSectionInEditMode ? (
                                    <>
                                        <p className='ApplicationVerification__reviewText'>In order to continue, you must save your changes above.</p>
                                        <div className='ApplicationVerification__confirmButtonContainer'>
                                            <Button id='app-verification-submit' className='ApplicationVerification__confirmButton' disabled>Confirm and Submit</Button>
                                        </div>

                                    </>

                                ) : (
                                    <>
                                        <p className='ApplicationVerification__reviewText'>I have reviewed my selections and I&#39;m ready to submit my application.</p>
                                        <div className='ApplicationVerification__confirmButtonContainer'>
                                            <Button id='app-verification-submit' className='ApplicationVerification__confirmButton'
                                                onClick={() => {
                                                    handleFormSubmit();
                                                }}>Confirm and Submit</Button>
                                        </div>
                                    </>
                                )}
                            </div>
                        </section>
                    </div>
                </div>
            )}
        </>
    );
};

export default ApplicationVerification;
